import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Phone } from "@phosphor-icons/react";
import { COMPANY } from "@/lib/content";
import { Reveal } from "@/components/Motion";

export const Chapter = ({ nr, label, dark = false }) => (
  <div className={`flex items-center gap-4 mb-8 ${dark ? "text-bone/50" : "text-soot"}`}>
    <span className="font-editorial italic text-lg text-signal">({nr})</span>
    <span className="text-[10px] uppercase tracking-[0.25em]">{label}</span>
    <span className={`flex-1 h-px ${dark ? "bg-bone/15" : "bg-ink/10"}`} />
  </div>
);

export const PageHero = ({ overline, title, lead, image }) => (
  <section className="pt-36 md:pt-44 pb-16 md:pb-24 max-w-[1440px] mx-auto px-5 md:px-10">
    <Reveal>
      <p className="text-[10px] uppercase tracking-[0.25em] text-signal font-semibold mb-6">{overline}</p>
    </Reveal>
    <Reveal delay={0.08}>
      <h1 className="font-cabinet text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-balance max-w-4xl">
        {title}
      </h1>
    </Reveal>
    {lead && (
      <Reveal delay={0.16}>
        <p className="mt-6 text-base md:text-lg text-soot max-w-2xl leading-relaxed">{lead}</p>
      </Reveal>
    )}
    {image && (
      <Reveal delay={0.24} className="mt-12">
        <div className="grain overflow-hidden">
          <img src={image} alt={typeof title === "string" ? title : overline} className="w-full h-[320px] md:h-[460px] object-cover" loading="eager" />
        </div>
      </Reveal>
    )}
  </section>
);

export const CtaBanner = ({ title, text }) => (
  <section data-testid="cta-banner" className="bg-signal text-bone grain">
    <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
      <div className="md:col-span-8">
        <Reveal>
          <h2 className="font-cabinet text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-balance">
            {title || "Har jeres virksomhed brug for en maler eller underentreprenør?"}
          </h2>
          <p className="mt-5 text-bone/80 text-base md:text-lg max-w-xl">
            {text || "Kontakt os for en dialog om opgaven – vi giver et konkret og uforpligtende tilbud."}
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="md:col-span-4 flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
        <Link
          to="/kontakt"
          data-testid="cta-quote-button"
          className="flex items-center justify-center gap-2 bg-ink text-bone font-semibold px-8 py-4 text-sm hover:bg-bone hover:text-ink transition-colors duration-300 w-full md:w-auto"
        >
          Indhent tilbud <ArrowUpRight size={16} weight="bold" />
        </Link>
        <a
          href={COMPANY.phoneHref}
          data-testid="cta-call-button"
          className="flex items-center justify-center gap-2 border border-bone/40 font-semibold px-8 py-4 text-sm hover:bg-bone hover:text-signal hover:border-bone transition-colors duration-300 w-full md:w-auto"
        >
          <Phone size={16} weight="bold" /> Ring {COMPANY.phoneDisplay}
        </a>
      </Reveal>
    </div>
  </section>
);

export const MarqueeStrip = ({ items, dark = false }) => (
  <div
    data-testid="marquee-strip"
    className={`py-5 border-y overflow-hidden ${dark ? "bg-ink text-bone/70 border-bone/10" : "bg-bone text-soot border-ink/10"}`}
  >
    <Marquee speed={28} gradient={false} pauseOnHover>
      {items.map((item, i) => (
        <span key={i} className="flex items-center">
          <span className="font-cabinet text-sm md:text-base font-medium uppercase tracking-[0.2em] px-6">
            {item}
          </span>
          <span className="w-1.5 h-1.5 rotate-45 bg-signal inline-block" />
        </span>
      ))}
    </Marquee>
  </div>
);

export const QuoteButtons = ({ className = "" }) => (
  <div className={`flex flex-wrap gap-3 ${className}`}>
    <Link
      to="/kontakt"
      data-testid="section-quote-button"
      className="flex items-center gap-2 bg-signal text-bone text-sm font-semibold px-7 py-3.5 group hover:bg-ink transition-colors duration-300"
    >
      Indhent tilbud
      <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </Link>
    <a
      href={COMPANY.phoneHref}
      data-testid="section-call-button"
      className="flex items-center gap-2 border border-ink/25 text-sm font-semibold px-7 py-3.5 hover:border-ink transition-colors duration-300"
    >
      <Phone size={16} weight="bold" /> Ring {COMPANY.phoneDisplay}
    </a>
  </div>
);
